import { InvisibleIcon, type TableColumns, Tooltip, VisibleIcon } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { BpLink } from 'components/BpLink/BpLink';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useConfirm } from 'hooks/useConfirm';
import { useTranslation } from 'react-i18next';
import { getExternalService } from 'utils/getExternalServiceHelper';
import { naturalStringSort, uppercaseFirstLetter } from 'utils/stringHelper';
import { GroupVisibility } from '../../../client/gql/graphql';
import { useRooms } from '../../../matrix/hooks/useRooms';
import type { CourseTableColumns, CourseTableType } from '../types/types';

export const UseCoursesTableFragment = graphql(`
  fragment UseCoursesTableFields on Query {
    courses: groups(options: { sort: { visibility: DESC, name: ASC } }, where: $courseWhere) {
      __typename
      uuid
      name
      managedBy
      isCommunity
      visibility
      showImportedLessonEvents
      showLessonEventsInClassbook
      subject {
        __typename
        uuid
        name
      }
      organization {
        __typename
        uuid
        displayName
      }
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            directLink
            lastSync
          }
        }
      }
      threemaBroadcastConnection {
        __typename
        edges {
          __typename
          properties {
            __typename
            enabled
            lastSync
          }
        }
      }
      externalServices {
        __typename
        threema {
          __typename
          enabled
          lastSync
        }
      }
      events(where: { virtualLocations_SOME: { running: true } }) {
        __typename
        uuid
      }
      admins {
        __typename
        uuid
        displayNameShort
        displayName
      }
      schoolYear {
        __typename
        uuid
        name
      }
      teachingUnitsConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
            assignments {
              __typename
              uuid
              submissions(where: { status: New }) {
                __typename
                uuid
              }
            }
          }
        }
      }
    }
  }
`);

type useCoursesTableProps = {
  query: FragmentType<typeof UseCoursesTableFragment> | undefined;
  showCourseInfo?: boolean;
  columns?: CourseTableColumns;
  onNavigate?: (courseUuid: string) => void;
};

export const useCoursesTable = (props?: useCoursesTableProps) => {
  const { t } = useTranslation();
  const rooms = useRooms();

  const { query, showCourseInfo, columns, onNavigate } = props ?? {};

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('courses.titleSingular'),
      context: 'male',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const fragment = getFragmentData(UseCoursesTableFragment, query);
  const courses = fragment?.courses;

  const assignments = courses?.flatMap((g) => g.teachingUnitsConnection.edges.flatMap((e) => e.node.assignments));

  function getNewMessagesForCourse(courseUuid: string): boolean {
    const notificationRoom = rooms?.find((room) => room.name.includes(courseUuid));
    let newMessages = false;

    if (notificationRoom?.getRoomUnreadNotificationCount) {
      newMessages = notificationRoom.getRoomUnreadNotificationCount() > 0;
    }

    if (!newMessages && assignments) {
      assignments.some((assignment) => {
        return assignment.submissions.some((submission) => {
          const room = rooms?.find((room) => room?.name?.includes(submission.uuid));
          return (room?.getRoomUnreadNotificationCount() ?? 0) > 0;
        });
      });
    }

    return newMessages;
  }

  /**
   * Maps incoming data from UseCoursesFragment to CourseTableType
   *
   * @returns {CourseTableType[]} An array of mapped data to use in course tables
   */
  function getTableData(): CourseTableType[] {
    return (
      courses?.map((course) => {
        const nextCloud = course.nextcloudConnection?.edges[0]?.properties;
        const threema = course.threemaBroadcastConnection?.edges[0]?.properties;

        const todoCount: number = course.teachingUnitsConnection.edges
          .flatMap((e) => {
            const newSubmsns = e.node.assignments.map((a) => a.submissions.length);
            return newSubmsns.reduce((a, b) => a + b, 0);
          })
          .reduce((a, b) => a + b, 0);

        return {
          uuid: course.uuid,
          organizationUuid: course.organization.uuid,
          organizationName: course.organization.displayName ?? '',
          subjectUuid: course.subject?.uuid ?? '',
          subjectName: course.subject?.name ?? '',
          name: course.name,
          managedBy: course.managedBy,
          meetingInProgress: course.events.length > 0,
          count: todoCount,
          newMessage: getNewMessagesForCourse(course.uuid),
          community: course.isCommunity,
          schoolyearUuid: course.schoolYear?.uuid ?? '',
          schoolyearName: course.schoolYear?.name ?? '',
          adminShortNames: course.admins.map((admin) => admin.displayNameShort ?? '').sort(naturalStringSort),
          adminNames: course.admins.map((admin) => admin.displayName ?? '').sort(naturalStringSort),
          origin: course.managedBy ? uppercaseFirstLetter(course.managedBy) : '-',
          nextcloud: {
            enabled: nextCloud?.enabled ?? false,
            lastSync: nextCloud?.lastSync ? dayjs(nextCloud?.lastSync) : undefined,
          },
          threema: {
            enabled: threema?.enabled ?? false,
            lastSync: threema?.lastSync ? dayjs(threema.lastSync) : undefined,
          },
          visibility: course.visibility,
        };
      }) ?? []
    ).sort((a, b) => naturalStringSort(a.name, b.name));
  }

  /**
   * Generates an array of table column configurations based on the selected columns.
   *
   * @returns {TableColumns<CourseTableType>[]} An array of column configurations for courses tables
   * including:
   * - name (with BpLink component and navigation)
   * - organization (with tooltip)
   * - admins (with tooltip showing full names)
   * - schoolYear
   * - nextcloud status
   * - threema status
   * - origin
   * - community flag
   * - visibility
   *
   * Each column is only included if specified in the `columns` parameter.
   */
  function getTableColumns(): TableColumns<CourseTableType>[] {
    const tableColumns: TableColumns<CourseTableType>[] = [];

    if (columns?.includes('name')) {
      tableColumns.push({
        header: t('common.designation'),
        id: 'name',
        accessorKey: 'name',
        cell: ({ row }) => {
          return (
            <BpLink
              value={row.original.name}
              onNavigate={() => onNavigate && onNavigate(row.original.uuid)}
              counter={showCourseInfo ? row.original.count : undefined}
              hasNews={showCourseInfo ? row.original.newMessage : undefined}
              inProgress={showCourseInfo ? row.original.meetingInProgress : undefined}
              // TODO: isEditorOfCourse, allowAfterDue
            />
          );
        },
        canExpand: true,
      });
    }

    if (columns?.includes('organization')) {
      tableColumns.push({
        header: t('institution.title'),
        id: 'organizationName',
        accessorKey: 'organizationName',
        size: 400,
        cell: ({ row }) => <Tooltip content={row.original.organizationName}>{row.original.organizationName}</Tooltip>,
      });
    }

    if (columns?.includes('subject')) {
      tableColumns.push({
        header: t('subjects.title', { count: 1 }),
        id: 'subjectName',
        accessorKey: 'subjectName',
        size: 400,
        cell: ({ row }) => <Tooltip content={row.original.subjectName}>{row.original.subjectName}</Tooltip>,
      });
    }

    if (columns?.includes('admins')) {
      tableColumns.push({
        header: t('common.administration'),
        id: 'admins',
        accessorKey: 'admins',
        cell: ({ row }) => (
          <Tooltip content={row.original.adminNames.join('\n')}>{row.original.adminShortNames.join(', ')}</Tooltip>
        ),
      });
    }

    if (columns?.includes('schoolYear')) {
      tableColumns.push({
        header: t('schoolyears.titleSingular'),
        id: 'schoolyearName',
        accessorKey: 'schoolyearName',
        size: 130,
      });
    }

    if (columns?.includes('origin')) {
      tableColumns.push({
        header: t('meetings.origin'),
        id: 'origin',
        accessorKey: 'origin',
      });
    }

    if (columns?.includes('nextcloud')) {
      tableColumns.push({
        header: t('common.nextcloud'),
        id: 'nextcloud',
        accessorKey: 'nextcloud',
        cell: ({ row }) => {
          const service = getExternalService(
            row.original.nextcloud?.enabled ?? false,
            row.original.nextcloud?.lastSync,
          );
          return <div style={{ color: service.color ?? undefined }}>{service.text.toString()}</div>;
        },
        size: 150,
      });
    }

    if (columns?.includes('threema')) {
      tableColumns.push({
        header: t('common.threema'),
        id: 'threema',
        accessorKey: 'threema',
        cell: ({ row }) => {
          const service = getExternalService(row.original.threema?.enabled ?? false, row.original.threema?.lastSync);
          return <div style={{ color: service.color ?? undefined }}>{service.text.toString()}</div>;
        },
        size: 150,
      });
    }

    if (columns?.includes('community')) {
      tableColumns.push({
        header: t('common.community'),
        id: 'community',
        accessorKey: 'community',
        type: 'boolean',
        size: 75,
      });
    }

    if (columns?.includes('visibility')) {
      tableColumns.push({
        header: t('common.visibility'),
        id: 'visibility',
        size: 55,
        accessorFn: (row) => row.visibility,
        cell: ({ row }) => (row.original.visibility === GroupVisibility.Hidden ? <InvisibleIcon /> : <VisibleIcon />),
      });
    }

    return tableColumns;
  }

  return {
    data: getTableData(),
    columns: getTableColumns(),
    ConfirmationDialog,
    confirmDelete,
  };
};
